/**
 * features/finance/ReminderWording.tsx
 * ------------------------------------------------------------------
 * What a parent actually reads.
 *
 * The bursar could always edit the message she copies into WhatsApp one
 * family at a time. The wording that went out in bulk — from the 07:00
 * sweep and from a class send, to every parent in arrears — was written
 * into a Cloud Function, so the one message the school sends most often was
 * the one nobody at the school could change. This is that screen.
 *
 * THE PREVIEW RENDERS THROUGH THE SAME CODE AS THE SENDER. It is not an
 * approximation of the email; lib/reminderTemplate.ts fills it in and
 * functions/lib/reminderTemplate.js fills in the real one, and a test
 * compares the two implementations' OUTPUT so they cannot drift. A preview
 * that renders differently from the sender is worse than no preview,
 * because it is a promise the system does not keep.
 *
 * IT PREVIEWS AGAINST A REAL DEBTOR where there is one. A message that
 * reads well against "Aone Molefe / P4,500.00 / 26 days" and badly against
 * a pupil with no recorded name is a message worth finding out about here.
 *
 * TWO LETTERS, NOT ONE. A payment falling due next week and a payment
 * already overdue are different conversations, and a school made to use one
 * form of words for both will pick the harsher one.
 * ------------------------------------------------------------------ */
import { useMemo, useState } from 'react';
import { Mail, Eye, RotateCcw, Save, AlertCircle, CheckCircle2 } from 'lucide-react';
import {
  DEFAULT_REMINDER_TEMPLATES, REMINDER_FIELDS, templatesFromSettings,
  settingsFromTemplates, renderReminderMessage, reminderTemplateProblem,
  isDefaultTemplate, type ReminderKind, type ReminderTemplates, type ReminderFacts,
} from '@/lib/reminderTemplate';
import { finance } from '@/features/finance/financeWrite';
import { SoftCard, PanelHeader, glassFieldClass, btnPrimary, btnGhost } from '@/features/finance/ui';
import type { FinanceSettings } from '@/lib/financeCore';

const KINDS: { id: ReminderKind; label: string; note: string }[] = [
  { id: 'after', label: 'Overdue', note: 'Sent when a payment has passed its due date. This is the one used by a class send.' },
  { id: 'before', label: 'Due soon', note: 'A courtesy note before the due date, sent by the daily 07:00 reminder.' },
];

export default function ReminderWording({
  settings, canEdit, sample,
}: {
  settings: FinanceSettings;
  canEdit: boolean;
  /** A real overdue row, so the preview is not a fiction. */
  sample?: ReminderFacts;
}) {
  const saved = useMemo(
    () => templatesFromSettings(settings as unknown as Record<string, unknown>),
    [settings],
  );

  const [kind, setKind] = useState<ReminderKind>('after');
  const [draft, setDraft] = useState<ReminderTemplates>(saved);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [open, setOpen] = useState(false);

  const current = draft[kind];
  const problem = reminderTemplateProblem(current);
  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);

  /* Stand-ins only where the school has nothing real to show. */
  const facts: ReminderFacts = {
    parent: 'Parent/Guardian',
    child: 'Aone Molefe',
    invoiceNo: 'INV/2026/0413',
    instalment: 'the Term 2 instalment',
    amount: 'P4,500.00',
    dueDate: '2026-08-10',
    daysOverdue: 26,
    ...sample,
    school: 'Livingstone Kolobeng College',
    motto: 'Ad Astra',
  };
  const preview = renderReminderMessage(current, facts);

  function set(patch: Partial<{ subject: string; body: string }>) {
    setDraft({ ...draft, [kind]: { ...current, ...patch } });
    setMsg(null);
  }

  async function save() {
    // Both letters are checked, not just the one on screen — the other tab
    // may hold an edit that was never looked at again.
    for (const k of ['after', 'before'] as ReminderKind[]) {
      const p = reminderTemplateProblem(draft[k]);
      if (p) {
        setKind(k);
        setMsg({ ok: false, text: `${k === 'after' ? 'Overdue' : 'Due soon'}: ${p}` });
        return;
      }
    }
    setBusy(true);
    try {
      await finance.settings.update(settingsFromTemplates(draft));
      setMsg({ ok: true, text: 'Saved. This is what parents will receive from now on.' });
    } catch (e) {
      setMsg({ ok: false, text: e instanceof Error ? e.message : String(e) });
    } finally { setBusy(false); }
  }

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className={btnGhost}>
        <Mail size={14} /> Edit the reminder wording
        {!isDefaultTemplate(saved.after, 'after') && (
          <span className="ml-1 text-xs text-gray-400">(edited)</span>
        )}
      </button>
    );
  }

  return (
    <SoftCard pad="p-0" hover={false}>
      <PanelHeader
        icon={Mail}
        title="Reminder wording"
        subtitle="What parents receive from a class send and the daily reminder"
        right={<button type="button" className={btnGhost} onClick={() => setOpen(false)}>Close</button>}
      />
      <div className="p-5 space-y-4">
        <div className="flex flex-wrap gap-2">
          {KINDS.map((k) => (
            <button
              key={k.id}
              type="button"
              onClick={() => { setKind(k.id); setMsg(null); }}
              className={kind === k.id ? btnPrimary : btnGhost}
            >
              {k.label}
              {!isDefaultTemplate(draft[k.id], k.id) && <span className="ml-1 text-xs opacity-70">•</span>}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {KINDS.find((k) => k.id === kind)?.note}
        </p>

        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Subject</label>
          <input
            value={current.subject}
            onChange={(e) => set({ subject: e.target.value })}
            disabled={!canEdit}
            className={glassFieldClass('py-2')}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Message</label>
          <textarea
            value={current.body}
            onChange={(e) => set({ body: e.target.value })}
            disabled={!canEdit}
            rows={12}
            className={glassFieldClass('py-2 font-mono text-xs leading-relaxed')}
          />
        </div>

        {/* The list of fields, shown rather than documented. A field the
            bursar cannot see is a field she cannot use. */}
        <div>
          <p className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">
            Fields you can use — click to insert the name, then type it where you want it
          </p>
          <div className="flex flex-wrap gap-1.5">
            {REMINDER_FIELDS.map((f) => (
              <span
                key={f.token}
                title={f.describes}
                className="text-xs font-mono px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 cursor-help"
              >
                {`{${f.token}}`}
              </span>
            ))}
          </div>
        </div>

        {problem && (
          <p className="text-sm text-amber-700 dark:text-amber-300 flex items-start gap-1.5">
            <AlertCircle size={14} className="mt-0.5 shrink-0" /> {problem}
          </p>
        )}

        {/* THE PREVIEW. Rendered by the same function the sender uses. */}
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800/60 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
            <Eye size={13} />
            What a parent sees{sample ? ' — using a real overdue account' : ''}
          </div>
          <div className="p-4 space-y-2">
            <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">{preview.subject}</p>
            <pre className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-sans">
              {preview.body}
            </pre>
            {/* SHOWN BECAUSE IT IS SENT. The school appends this to every
                reminder and it cannot be edited here, but a preview that
                leaves it out is a preview that lies about what the parent
                receives — which this file's own mirror exists to prevent. */}
            <p className="text-[13px] text-gray-600 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700 pt-3 mt-3 leading-relaxed">
              <strong className="text-gray-800 dark:text-gray-200">This reminder is sent automatically.</strong>
              {' '}If you have already paid, or you have an agreed payment arrangement with
              the school, please ignore it — your payment may have crossed with this
              message. If you think it has reached you in error, please contact the
              bursar’s office and we will check your account.
            </p>
            <p className="text-[11px] text-gray-400 italic">
              Added to every reminder automatically. It cannot be edited or removed here.
            </p>
          </div>
        </div>

        {msg && (
          <div className={`rounded-lg px-4 py-3 text-sm flex items-start gap-2 ${msg.ok
            ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200'
            : 'bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-200'}`}
          >
            {msg.ok ? <CheckCircle2 size={16} className="mt-0.5 shrink-0" /> : <AlertCircle size={16} className="mt-0.5 shrink-0" />}
            <span>{msg.text}</span>
          </div>
        )}

        {canEdit && (
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" className={btnPrimary} disabled={busy || !!problem || !dirty} onClick={() => { void save(); }}>
              <Save size={14} /> {busy ? 'Saving…' : 'Save wording'}
            </button>
            <button
              type="button"
              className={btnGhost}
              onClick={() => { setDraft({ ...draft, [kind]: DEFAULT_REMINDER_TEMPLATES[kind] }); setMsg(null); }}
            >
              <RotateCcw size={14} /> Restore the original wording
            </button>
            {dirty && <span className="text-xs text-amber-600 dark:text-amber-400">Unsaved changes</span>}
          </div>
        )}
        {!canEdit && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            You can read this wording but not change it. Only the bursar and finance
            administrators may edit what parents receive.
          </p>
        )}
      </div>
    </SoftCard>
  );
}
